import { useEffect, useRef, useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { ChevronDown, LogOut, Menu, Search, Settings, User, Shield, Home, Wrench, MessageCircle, LifeBuoy, Users, Calendar, Award, Bookmark } from "lucide-react";
import LogoMark from "./LogoMark";
import NotificationBell from "./NotificationBell";
import AuthImage from "./AuthImage";
import { useAuth } from "../contexts/AuthContext";
import { api } from "../lib/api";

export const SIDEBAR_LINKS = [
  { to: "/home", label: "Home", icon: Home, testid: "nav-home" },
  { to: "/saved", label: "Saved Posts", icon: Bookmark, testid: "nav-saved" },
  { to: "/tools", label: "Cyber Investigation Tools", icon: Wrench, testid: "nav-tools" },
  { to: "/chat", label: "Chat", icon: MessageCircle, testid: "nav-chat" },
  { to: "/tickets", label: "Tickets", icon: LifeBuoy, testid: "nav-tickets" },
  { to: "/groups", label: "Groups", icon: Users, testid: "nav-groups" },
  { to: "/events", label: "Events", icon: Calendar, testid: "nav-events" },
];

const ADMIN_LINKS = [
  { to: "/admin", label: "Admin", icon: Shield, testid: "nav-admin" },
  { to: "/admin/badges", label: "Badges", icon: Award, testid: "nav-admin-badges" },
  { to: "/admin/tickets", label: "Admin Tickets", icon: LifeBuoy, testid: "nav-admin-tickets" },
];

export default function NavBar({ onToggleSidebar }) {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [showResults, setShowResults] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const debounceRef = useRef(null);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!query.trim()) { setResults([]); return; }
    debounceRef.current = setTimeout(async () => {
      try {
        const { data } = await api.get(`/search`, { params: { q: query } });
        setResults(data.results || []);
        setShowResults(true);
      } catch (err) {
        // Search is best-effort; surface as console only so we don't spam toasts.
        console.warn("[nav-search] failed:", err?.response?.status || err?.message);
        setResults([]);
      }
    }, 250);
    return () => clearTimeout(debounceRef.current);
  }, [query]);

  const handleLogout = async () => { await signOut(); navigate("/", { replace: true }); };

  const initials = (user?.full_name || user?.email || "?").split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase();

  return (
    <div className="sticky top-0 z-50 bg-[#050d1a]/95 backdrop-blur-md border-b border-[rgba(0,170,255,0.18)]">
      <div className="h-16 flex items-center gap-3 px-4 lg:px-6">
        <button onClick={onToggleSidebar} className="csc-btn-ghost !p-2 lg:hidden" aria-label="Toggle navigation" data-testid="sidebar-toggle">
          <Menu size={18} />
        </button>
        <LogoMark size={34} withText={false} to="/home" />

        <div className="relative mx-auto" style={{ width: "min(100%, 320px)" }} data-testid="nav-search-wrapper">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#94a3b8]" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onFocus={() => setShowResults(true)}
            onBlur={() => setTimeout(() => setShowResults(false), 200)}
            placeholder="Search…"
            className="csc-input pl-9 !py-2"
            data-testid="nav-search-input"
          />
          {showResults && results.length > 0 && (
            <div className="absolute top-full mt-2 w-full csc-card max-h-96 overflow-auto" data-testid="search-results">
              {results.map((r) => (
                <div key={`${r.type}-${r.id}`} className="flex items-center gap-3 px-4 py-2.5 hover:bg-[#00aaff]/10 cursor-pointer border-b border-[rgba(0,170,255,0.18)] last:border-none" onMouseDown={() => navigate(r.handle ? `/u/${r.handle}` : (r.type === "user" ? `/profile/${r.id}` : `/org/${r.id}`))} data-testid={`search-result-${r.type}-${r.id}`}>
                  <div className="h-8 w-8 rounded-full bg-[#0d1b2e] border border-[rgba(0,170,255,0.18)] flex items-center justify-center font-mono-tech text-xs">
                    {r.type === "user" ? <User size={14} className="text-[#39ff14]" /> : <Shield size={14} className="text-[#00aaff]" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-white truncate">{r.name}</div>
                    <div className="text-[11px] font-mono-tech text-[#94a3b8] truncate">{r.subtitle}</div>
                  </div>
                  <span className="text-[10px] font-mono-tech uppercase tracking-wider text-[#94a3b8]">{r.type}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="flex items-center gap-2 lg:gap-3">
          <Link to="/donate" className="csc-nav-btn csc-nav-btn-secondary csc-pulse" data-testid="top-donate-btn">Donate</Link>
          <Link
            to="/about"
            className="csc-nav-btn csc-nav-btn-primary hidden md:inline-flex"
            style={{ backgroundColor: "#00aaff", color: "#ffffff", border: "1px solid transparent" }}
            data-testid="top-about-btn"
          >
            About / Contact
          </Link>
          <NotificationBell />
          <div className="relative">
            <button onClick={() => setShowMenu((v) => !v)} className="flex items-center gap-2 px-2 py-1 rounded border border-transparent hover:border-[rgba(0,170,255,0.18)]" data-testid="avatar-dropdown-btn">
              <div className="h-8 w-8 rounded-full overflow-hidden border border-[rgba(0,170,255,0.18)] bg-gradient-to-br from-[#00aaff] to-[#39ff14]">
                <AuthImage src={user?.avatar_url} alt="" className="w-full h-full object-cover" fallback={<div className="w-full h-full flex items-center justify-center text-[#050d1a] font-mono-tech text-xs font-semibold">{initials}</div>} />
              </div>
              <ChevronDown size={14} className="text-[#94a3b8]" />
            </button>
            {showMenu && (
              <div className="absolute right-0 mt-2 w-60 csc-card overflow-hidden" onMouseLeave={() => setShowMenu(false)} data-testid="avatar-dropdown-menu">
                <div className="px-4 py-3 border-b border-[rgba(0,170,255,0.18)]">
                  <div className="text-sm font-medium text-white truncate">{user?.full_name}</div>
                  <div className="text-[11px] font-mono-tech text-[#94a3b8] truncate">{user?.email}</div>
                </div>
                <Link to={user?.handle ? `/u/${user.handle}` : "/profile/me"} className="flex items-center gap-2 px-4 py-2.5 text-sm hover:bg-[#00aaff]/10" data-testid="menu-profile"><User size={14} /> Profile</Link>
                <Link to="/settings" className="flex items-center gap-2 px-4 py-2.5 text-sm hover:bg-[#00aaff]/10" data-testid="menu-settings"><Settings size={14} /> Settings</Link>
                {user?.managed_org_id && (<Link to="/org/manage" className="flex items-center gap-2 px-4 py-2.5 text-sm text-[#00aaff] hover:bg-[#00aaff]/10" data-testid="menu-manage-org"><Shield size={14} /> Manage Organization: {user.managed_org_name || ""}</Link>)}
                {user?.role === "admin" && (<Link to="/admin" className="flex items-center gap-2 px-4 py-2.5 text-sm text-[#39ff14] hover:bg-[#39ff14]/10" data-testid="menu-admin"><Shield size={14} /> Admin Console</Link>)}
                <button onClick={handleLogout} className="w-full text-left flex items-center gap-2 px-4 py-2.5 text-sm text-red-400 hover:bg-red-500/10 border-t border-[rgba(0,170,255,0.18)]" data-testid="menu-logout"><LogOut size={14} /> Logout</button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export function Sidebar({ open, onClose }) {
  const { user } = useAuth();
  const links = [...SIDEBAR_LINKS, ...(user?.role === "admin" ? ADMIN_LINKS : [])];
  return (
    <>
      {/* Mobile backdrop */}
      {open && <div className="fixed inset-0 z-40 bg-black/60 lg:hidden" onClick={onClose} />}
      <aside
        className={`fixed lg:sticky top-16 left-0 bottom-0 lg:bottom-auto lg:h-[calc(100vh-4rem)] z-40 w-[220px] bg-[#0d1b2e] border-r border-[rgba(0,170,255,0.18)] overflow-y-auto transition-transform ${open ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}`}
        data-testid="sidebar"
      >
        <nav className="py-4 flex flex-col">
          {links.map((l) => {
            const Icon = l.icon;
            return (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === "/admin"}
                onClick={onClose}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-5 py-3 text-sm border-l-4 transition-colors ${
                    isActive
                      ? "border-[#00aaff] text-[#00aaff] bg-[#00aaff]/8"
                      : "border-transparent text-[#94a3b8] hover:text-white hover:bg-white/[0.03]"
                  }`
                }
                data-testid={l.testid}
              >
                <Icon size={16} />
                <span className="truncate">{l.label}</span>
              </NavLink>
            );
          })}
        </nav>
      </aside>
    </>
  );
}
